import React, { useEffect, type JSX, type ReactNode } from 'react';

import { PortaledLayer } from './PortaledLayer';
import {
  copyVerificationCsvToClipboard,
  downloadVerificationCsv,
} from './VerificationTableActions';

type Props = {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  metaLine?: string;
  csvText: string;
  downloadFilenameBase: string;
  csvToastLabel: string;
  children: ReactNode;
};

/** Full-screen table view with copy/export (used from race transition verification panels). */
export default function VerificationTableExpandDialog(props: Props): JSX.Element | null {
  const {
    open,
    onClose,
    title,
    subtitle,
    metaLine,
    csvText,
    downloadFilenameBase,
    csvToastLabel,
    children,
  } = props;

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const BTN =
    'rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800';

  return (
    <PortaledLayer>
      <div
        className="fixed inset-0 z-[5100] flex items-center justify-center bg-black/40 p-3 dark:bg-black/70 sm:p-6"
        role="dialog"
        aria-modal="true"
        aria-labelledby="verification-table-expand-title"
        onClick={(e) => {
          if (e.target === e.currentTarget) onClose();
        }}
      >
        <div
          className="flex max-h-[min(95vh,1100px)] w-full max-w-[min(1600px,100vw-1.5rem)] flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-2xl dark:border-slate-700 dark:bg-slate-900"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex shrink-0 flex-wrap items-center justify-between gap-3 border-b border-gray-200 bg-gray-50 px-4 py-3 dark:border-slate-700 dark:bg-slate-800">
            <div className="min-w-0">
              <h2 id="verification-table-expand-title" className="text-sm font-semibold text-gray-900 dark:text-slate-100">
                {title}
              </h2>
              {subtitle ? (
                <p className="mt-0.5 break-words text-xs text-gray-500 dark:text-slate-400">{subtitle}</p>
              ) : null}
              {metaLine ? (
                <p className="mt-0.5 text-xs text-gray-500 dark:text-slate-400">{metaLine}</p>
              ) : null}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                className={BTN}
                onClick={() => void copyVerificationCsvToClipboard(csvText, csvToastLabel)}
              >
                Copy CSV
              </button>
              <button
                type="button"
                className={BTN}
                onClick={() => downloadVerificationCsv(csvText, downloadFilenameBase)}
              >
                Export CSV
              </button>
              <button type="button" className={BTN} onClick={onClose}>
                Close
              </button>
            </div>
          </div>
          <div className="min-h-0 flex-1 overflow-auto bg-white px-4 py-3 dark:bg-slate-900">{children}</div>
        </div>
      </div>
    </PortaledLayer>
  );
}
